import {
  QueryKey,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query'
import { QueryData, QueryFnData } from 'queries/types'

type MutationOptions<
  TData,
  TError,
  TVariables,
  TContext extends unknown[]
> = Partial<UseMutationOptions<TData, TError, TVariables, TContext>>

/**
 * Cache data for a query hook (`data`) or API/queryFn (awaited return).
 * Prefer the queryFn when the hook uses `select` — cache stores queryFn data.
 */
type OptimisticQueryData<TQuery> = TQuery extends (...args: never[]) => {
  data: unknown
}
  ? QueryData<TQuery>
  : TQuery extends (...args: never[]) => unknown
  ? QueryFnData<TQuery>
  : never

type CacheUpdater<TVariables, TQueryData> = {
  queryKey: QueryKey
  updater: (
    data: TVariables,
    previousState: TQueryData | undefined
  ) => TQueryData | undefined
}

type UpdatersFor<TVariables, TContext extends unknown[]> = {
  [I in keyof TContext]: CacheUpdater<TVariables, TContext[I]>
}

// Infer TVariables / TContext from `options` only. Without this, unannotated
// updater callbacks participate in inference and collapse those params to `any`.
type NoInfer<T> = [T][T extends unknown ? 0 : never]

/**
 * Typed optimistic updater for a query hook (or queryFn).
 * Pass `typeof` the query to infer `previous`; pass mutation variables as the
 * second type arg.
 *
 * @example
 * ```ts
 * const updateTipHistory: OptimisticUpdater<
 *   typeof useTipHistory,
 *   TipVariables
 * > = {
 *   queryKey: workerKeys.tipHistory(workerId),
 *   updater: (variables, previous) => { ... },
 * }
 * ```
 */
export type OptimisticUpdater<TQuery, TVariables = unknown> = CacheUpdater<
  TVariables,
  OptimisticQueryData<TQuery>
>

/**
 * Returns `onMutate` / `onError` / `onSuccess` handlers that apply optimistic
 * cache writes, roll them back on error, and invalidate on success.
 *
 * Spread the result into `useMutation`. `TContext` is the array of prior cache
 * snapshots, in `byKey` order.
 *
 * @example
 * ```ts
 * const updateTipHistory = useTipHistoryOptimisticUpdate(workerId)
 *
 * useMutation({
 *   mutationFn: sendTip,
 *   ...useOptimisticUpdate(options, [updateTipHistory]),
 * })
 * ```
 */
export const useOptimisticUpdate = <
  TData,
  TError,
  TVariables,
  TContext extends unknown[]
>(
  options: MutationOptions<TData, TError, TVariables, TContext>,
  byKey: NoInfer<UpdatersFor<TVariables, TContext>>
) => {
  const queryClient = useQueryClient()

  return {
    ...options,
    onMutate: async (data: TVariables): Promise<TContext> => {
      await Promise.all(
        byKey.map(({ queryKey }) => queryClient.cancelQueries({ queryKey }))
      )

      const snapshot = <TQueryData>({
        queryKey,
        updater,
      }: CacheUpdater<TVariables, TQueryData>): TQueryData | undefined => {
        const previous = queryClient.getQueryData<TQueryData>(queryKey)
        queryClient.setQueryData<TQueryData>(queryKey, (current) =>
          updater(data, current)
        )
        return previous
      }

      options.onMutate?.(data)

      return byKey.map(snapshot) as TContext
    },
    onError: (error, variables, context) => {
      byKey.forEach(({ queryKey }, index) => {
        queryClient.setQueryData(queryKey, context?.[index])
      })
      options.onError?.(error, variables, context)
    },
    onSuccess: (data, variables, context) => {
      byKey.forEach(({ queryKey }) => {
        queryClient.invalidateQueries({ queryKey })
      })
      options.onSuccess?.(data, variables, context)
    },
  } satisfies typeof options
}
